import { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { useUserData } from "store/slices/authSlice";
import { useCountryList } from "store/slices/paymentMethodSlice";
import { CountryOption } from "types/common";
import { getStateName } from "utils/HelperFunctions";

interface AddressCompProps {
  orderData: any;
  cppData: any;
  isLoading: boolean;
  addressData?: any;
  hideForWireTransfer?: boolean;
}

interface DisplayAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const hasValue = (values: unknown[]) => values.some(Boolean);

const AddressComp = ({
  orderData,
  cppData,
  isLoading,
  addressData,
  hideForWireTransfer = false,
}: AddressCompProps) => {
  const userData = useUserData();
  const countryList: CountryOption[] = useCountryList();

  const address = useMemo(() => {
    if (isLoading) {
      return null;
    }

    const accountData = userData?.data?.accountData;
    let data: DisplayAddress | null = null;

    if (
      hasValue([
        cppData?.billing_address,
        cppData?.billing_address2,
        cppData?.billing_city,
        cppData?.billing_state,
        cppData?.billing_zip,
        cppData?.billing_country,
      ])
    ) {
      data = {
        line1: cppData?.billing_address || "",
        line2: cppData?.billing_address2 || "",
        city: cppData?.billing_city || "",
        state: cppData?.billing_state
          ? getStateName(cppData.billing_state, cppData?.billing_country) || ""
          : "",
        postalCode: cppData?.billing_zip || "",
        country: cppData?.billing_country || "",
      };
    } else if (
      hasValue([
        orderData?.billing_address1,
        orderData?.billing_address2,
        orderData?.billing_city,
        orderData?.billing_state,
        orderData?.billing_zip,
        orderData?.billing_country,
      ])
    ) {
      data = {
        line1: orderData?.billing_address1 || "",
        line2: orderData?.billing_address2 || "",
        city: orderData?.billing_city || "",
        state: orderData?.billing_state
          ? getStateName(orderData.billing_state, orderData?.billing_country) ||
            ""
          : "",
        postalCode: orderData?.billing_zip || "",
        country: orderData?.billing_country || "",
      };
    } else if (
      hasValue([
        addressData?.address1,
        addressData?.address2,
        addressData?.city,
        addressData?.state,
        addressData?.zip,
        addressData?.country,
      ])
    ) {
      data = {
        line1: addressData?.address1 || "",
        line2: addressData?.address2 || "",
        city: addressData?.city || "",
        state: addressData?.state
          ? getStateName(addressData.state, addressData?.country) || ""
          : "",
        postalCode: addressData?.zip || "",
        country: addressData?.country || "",
      };
    } else if (
      hasValue([
        accountData?.address1,
        accountData?.address2,
        accountData?.city,
        accountData?.state,
        accountData?.zip,
        accountData?.country,
      ])
    ) {
      data = {
        line1: accountData?.address1 || "",
        line2: accountData?.address2 || "",
        city: accountData?.city || "",
        state: accountData?.state
          ? getStateName(accountData.state, accountData?.country) || ""
          : "",
        postalCode: accountData?.zip || "",
        country: accountData?.country || "",
      };
    }

    if (!data?.country) {
      return data;
    }

    const countryInfo = countryList.find(
      (country) =>
        country.value.toLowerCase() === data?.country.toLowerCase() ||
        country.name.toLowerCase() === data?.country.toLowerCase()
    );

    return {
      ...data,
      country: countryInfo?.name || data.country,
    };
  }, [addressData, countryList, cppData, isLoading, orderData, userData]);

  if (
    hideForWireTransfer &&
    (orderData?.payment_method === "WT" ||
      orderData?.payment_method === "WTO") &&
    !isLoading
  ) {
    return <div className="h-[1px] w-full bg-strokePrimary" />;
  }

  return (
    <>
      <div className="h-[1px] w-full bg-strokePrimary" />
      <div className="flex items-start justify-between w-full">
        <div className="font-normal text-sm leading-normal text-txtSecondary whitespace-nowrap">
          Billing address
        </div>
        {isLoading ? (
          <div
            aria-busy="true"
            data-testid="billing-address-value"
            className="font-normal text-sm leading-[150%] text-txtPrimary text-right max-w-[200px]"
          >
            <Skeleton width={80} height={14} />
            <Skeleton width={80} height={14} />
            <Skeleton width={120} height={14} />
            <Skeleton width={130} height={14} />
            <Skeleton width={130} height={14} />
          </div>
        ) : address ? (
          <div
            data-testid="billing-address-value"
            className="font-normal text-sm leading-[150%] text-txtPrimary text-right max-w-[200px]"
          >
            {address.line1} {address.line1 ? <br /> : null}
            {address.line2} {address.line2 ? <br /> : null}
            {address.city} {address.city ? <br /> : null}
            {`${address.state} ${address.postalCode}`}
            {address.state || address.postalCode ? <br /> : null}
            {address.country}
          </div>
        ) : (
          <div
            data-testid="billing-address-value"
            className="font-normal text-sm leading-normal text-txtPrimary text-right max-w-[200px]"
          >
            --
          </div>
        )}
      </div>
    </>
  );
};

export default AddressComp;
